import React, { useState, useMemo } from "react";
import {
    Table, TableHead, TableBody, TableRow, TableCell,
    TablePagination, TableSortLabel, IconButton, Tooltip,
} from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { useTranslation } from 'react-i18next';
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import styles from "./AssignmentsTable.module.scss";

function GenericTable({
    data = [],
    columns = [],
    title = "My Table",
    noDataMessage = "No data",
    filterFn,
    onRowClick,
    onDropStatus,
    tableRef,
    clickedTable,
    handleChangeClickedTable,
    whatTable,
    defaultSort = { key: "", direction: "ascending" },
    defaultRowsPerPage = 100,
    rowsPerPageOptions = [5, 10, 25, 50, 100],
}) {
    const { t } = useTranslation();

    /* ---------- filtering ---------- */
    const filteredData = useMemo(
        () => (filterFn ? data.filter(filterFn) : data),
        [data, filterFn]
    );

    /* ---------- sorting ---------- */
    const [sortConfig, setSortConfig] = useState(defaultSort);
    const sortedData = useMemo(() => {
        const arr = [...filteredData];
        const { key, direction } = sortConfig;
        if (!key) return arr;

        arr.sort((a, b) => {
            const aVal = a[key];
            const bVal = b[key];
            if (typeof aVal === "string") {
                return direction === "ascending"
                    ? aVal?.localeCompare(bVal)
                    : bVal?.localeCompare(aVal);
            }
            if (typeof aVal === "number") {
                return direction === "ascending" ? aVal - bVal : bVal - aVal;
            }
            return 0;
        });
        return arr;
    }, [filteredData, sortConfig]);

    /* ---------- pagination ---------- */
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const paginated = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    const handleSort = (col) => {
        if (!col.field) return;
        setSortConfig((prev) =>
            prev.key === col.field
                ? { ...prev, direction: prev.direction === "ascending" ? "descending" : "ascending" }
                : { key: col.field, direction: "ascending" }
        );
    };

    /* ---------- dnd ---------- */
    const [isDragOver, setIsDragOver] = useState(false);
    const dragOver = (e) => {
        e.preventDefault();
        if (!isDragOver) setIsDragOver(true);
    };
    const dragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
    };
    const drop = (e) => {
        setIsDragOver(false);
        onDropStatus?.(e.dataTransfer.getData("text/plain"));
    };

    const isExpanded = clickedTable === whatTable;

    return (
        <div
            className={[styles.card, styles[whatTable], isDragOver && styles.dragOver].filter(Boolean).join(" ")}
            onDragOver={dragOver}
            onDragLeave={dragLeave}
            onDrop={drop}
        >
            <div className={styles.header}>
                <h3 className={styles.title}>
                    {title}
                    <span className={styles.count}>{filteredData.length}</span>
                </h3>
                <Tooltip title={isExpanded ? t("Collapse") : t("Expand")}>
                    <IconButton
                        size="small"
                        className={styles.expandBtn}
                        onClick={() => handleChangeClickedTable?.(whatTable)}
                    >
                        {isExpanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </div>

            <div className={styles.tableWrap} ref={tableRef}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell key={col.label} className={styles.headCell}>
                                    {col.field ? (
                                        <TableSortLabel
                                            active={sortConfig.key === col.field}
                                            direction={sortConfig.key === col.field && sortConfig.direction === "descending" ? "desc" : "asc"}
                                            onClick={() => handleSort(col)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    ) : col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginated.length ? (
                            paginated.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className={styles.row}
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData("text/plain", row.id)}
                                    onClick={() => onRowClick?.(row)}
                                >
                                    {columns.map((col) => (
                                        <TableCell key={col.field || col.label} className={styles.cell}>
                                            {col.render ? col.render(row) : row[col.field]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className={styles.empty}>
                                    {noDataMessage}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <TablePagination
                component="div"
                className={styles.pagination}
                count={filteredData.length}
                page={page}
                onPageChange={(e, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                labelRowsPerPage={t('RowsPerPage')}
                rowsPerPageOptions={rowsPerPageOptions}
            />
        </div>
    );
}

export default React.memo(GenericTable);
